import { Portal } from '@headlessui/react';
import React, {
    useEffect,
    useRef,
    ReactNode,
    KeyboardEvent,
    useState,
} from 'react';

interface BottomModalProps {
    isOpen: boolean;
    onClose?: () => void;
    children: ReactNode;
    contentWrapperClassName?: string;
    showBackDrop?: boolean;
}

export const BottomModal: React.FC<BottomModalProps> = ({
    isOpen,
    onClose,
    children,
    contentWrapperClassName = 'h-1/2',
    showBackDrop = true,
}) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const previousActiveElement = useRef<Element | null>(null);
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [hide, setHide] = useState(false);

    const handleClose = () => {
        if (onClose) {
            setHide(true);
            setTimeout(() => {
                onClose();
                document.body.style.overflow = 'initial';
                setIsVisible(false);
            }, 700); // Match animation duration
        }
    };
    // Manage modal visibility and focus
    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
            setHide(false);

            previousActiveElement.current = document.activeElement;
            modalRef.current?.focus();

            // Prevent background scrolling
            document.body.style.overflow = 'hidden';
        } else {
            // Trigger closing animation
            setHide(true);

            // Restore focus and background scroll after animation
            const timeout = setTimeout(() => {
                setIsVisible(false);
                if (previousActiveElement.current instanceof HTMLElement) {
                    previousActiveElement.current.focus();
                }
                document.body.style.overflow = '';
            }, 700); // Match animation duration

            return () => clearTimeout(timeout);
        }
        return () => null;
    }, [isOpen]);

    // Close modal on Escape key press
    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        e.stopPropagation();
        if (e.key === 'Escape') handleClose();
    };

    if (!isVisible) return null;

    return (
        <Portal>
            {/* Modal backdrop */}

            <div
                className={`fixed inset-0 z-40 transition-opacity duration-500 ${
                    hide ? 'opacity-0' : 'opacity-100'
                } ${
                    showBackDrop &&
                    'bg-black bg-opacity-50 dark:bg-modal-backdrop-dark'
                }`}
                onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                }}
                aria-hidden="true"
            />

            {/* Modal content */}
            <div
                className="fixed inset-x-0 bottom-0 flex items-center justify-center z-50"
                role="dialog"
                aria-modal="true"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={handleKeyDown}
            >
                <div
                    className={`animate__animated ${
                        hide ? 'animate__slideOutDown' : 'animate__slideInUp'
                    }`}
                >
                    <div
                        ref={modalRef}
                        tabIndex={-1}
                        className={`bg-base-200 pb-3 md:pb-0  overflow-x-hidden overflow-y-hidden rounded-t-3xl shadow-lg  w-full max-h-lvh md:w-1/2 md:h-full transform transition-transform duration-300 ${contentWrapperClassName}`}
                    >
                        <div>{children}</div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};
